import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Archive,
  SwapHoriz,
  DeleteSweep,
  ViewList,
  ShoppingBasket,
  Restaurant,
  LocalShipping,
  Business,
  HomeWork,
  Build,
  AttachMoney,
  ShowChart,
} from '@material-ui/icons';
import { Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import UserRoleType from 'types/enums/UserRoleType';
import SelectField from 'components/SelectField';
import Venue from 'types/models/Venue';
import { bulkSetStockModule, getSimpleVenues } from 'services/venuesServices';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import 'styles/pages/StockModuleHome.scss';

// Secciones visibles para el editor de depósito (rol 6): solo lo que opera en su depósito.
const WAREHOUSE_EDITOR_SECTION_NAMES = [
  'app-header-stock-warehouses',
  'app-header-stock-movements',
  'app-header-stock-losses',
  'app-header-stock-profitability',
];

const stockSections = [
  {
    name: 'app-header-stock-receptions',
    icon: <Archive style={{ fontSize: 48, color: '#4CAF50' }} />,
    color: '#4CAF50',
    redirect: '/private/stock/receptions',
  },
  {
    name: 'app-header-stock-movements',
    icon: <SwapHoriz style={{ fontSize: 48, color: '#2196F3' }} />,
    color: '#2196F3',
    redirect: '/private/stock/movements',
  },
  {
    name: 'app-header-stock-losses',
    icon: <DeleteSweep style={{ fontSize: 48, color: '#ec2121' }} />,
    color: '#ec2121',
    redirect: '/private/stock/movements?type=LOSS',
  },
  {
    name: 'app-header-stock-catalog',
    icon: <ViewList style={{ fontSize: 48, color: '#9C27B0' }} />,
    color: '#9C27B0',
    redirect: '/private/stock/catalog',
  },
  {
    name: 'app-header-stock-global-products',
    icon: <ShoppingBasket style={{ fontSize: 48, color: '#FF9800' }} />,
    color: '#FF9800',
    redirect: '/private/stock/global-products',
  },
  {
    name: 'app-header-stock-recipes',
    icon: <Restaurant style={{ fontSize: 48, color: '#FF5722' }} />,
    color: '#FF5722',
    redirect: '/private/stock/recipes',
  },
  {
    name: 'app-header-stock-productions',
    icon: <Build style={{ fontSize: 48, color: '#009688' }} />,
    color: '#009688',
    redirect: '/private/stock/productions',
  },
  {
    name: 'app-header-stock-suppliers',
    icon: <LocalShipping style={{ fontSize: 48, color: '#607D8B' }} />,
    color: '#607D8B',
    redirect: '/private/stock/suppliers',
  },
  {
    name: 'app-header-stock-warehouses',
    icon: <Business style={{ fontSize: 48, color: '#795548' }} />,
    color: '#795548',
    redirect: '/private/stock/warehouses',
  },
  {
    name: 'app-header-stock-production-centers',
    icon: <HomeWork style={{ fontSize: 48, color: '#3F51B5' }} />,
    color: '#3F51B5',
    redirect: '/private/stock/production-centers',
  },
  {
    name: 'app-header-stock-profitability',
    icon: <AttachMoney style={{ fontSize: 48, color: '#3ca832' }} />,
    color: '#3ca832',
    redirect: '/private/stock/profitability',
  },
  {
    name: 'app-header-stock-holding-gain-loss',
    icon: <ShowChart style={{ fontSize: 48, color: '#00838f' }} />,
    color: '#00838f',
    redirect: '/private/stock/holding-gain-loss',
  },
];

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authData } = React.useContext(AppContext);
  const isWarehouseEditor = authData?.user?.role === UserRoleType.WarehouseEditor;
  const isAdmin = authData?.user?.role === UserRoleType.Admin;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [selectedVenueIds, setSelectedVenueIds] = React.useState<string[]>([]);
  const [isApplying, setIsApplying] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [saveSuccess, setSaveSuccess] = React.useState('');

  const loadVenues = React.useCallback(async () => {
    const response = await getSimpleVenues();
    if (!response) return;
    setVenues(response);
    setSelectedVenueIds(response.filter((venue) => venue.usesStockModule).map((venue) => venue.id));
  }, []);

  React.useEffect(() => {
    if (isAdmin) {
      loadVenues();
    }
  }, [isAdmin, loadVenues]);

  const handleApply = async () => {
    setIsApplying(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const result = await bulkSetStockModule(selectedVenueIds);
      if (!result) {
        setSaveError(t('stock.home.error'));
      } else {
        setSaveSuccess(t('stock.home.success'));
        await loadVenues();
      }
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      setIsApplying(false);
    }
  };

  const handleVenuesBlur = () => {
    setSaveError('');
    setSaveSuccess('');
  };

  const venueOptions = mapToSelectOptions(venues, { label: 'name', value: 'id' });

  const sections = isWarehouseEditor
    ? stockSections.filter((section) => WAREHOUSE_EDITOR_SECTION_NAMES.includes(section.name))
    : stockSections;

  return (
    <div className="stock-module-container">
      {isAdmin && (
        <section className="stock-module-admin-panel ">
          <Typography className="stock-module-admin-panel-title">{t('stock.home.admin-title')}</Typography>
          <Typography className="stock-module-admin-panel-hint">{t('stock.home.hint')}</Typography>
          <SelectField
            name="venueIds"
            label={t('stock.home.venues-label')}
            placeholder={t('stock.home.venues-placeholder')}
            value={selectedVenueIds}
            options={venueOptions}
            isMulti={true}
            setFieldValue={(name, value) => setSelectedVenueIds(value as string[])}
            onBlur={handleVenuesBlur}
            touched={false}
            error={undefined}
            isClearable={true}
            backspaceRemovesValue={true}
            isDisabled={isApplying}
            isSearchable={true}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={isApplying || selectedVenueIds.length === 0}
            onClick={handleApply}
          >
            {t('stock.home.apply')}
          </Button>
          {saveError && <Typography className="stock-module-panel-error">{saveError}</Typography>}
          {saveSuccess && !saveError && <Typography className="stock-module-panel-success">{saveSuccess}</Typography>}
        </section>
      )}
      {sections.map((section, i) => {
        return (
          <article
            key={i}
            className="card"
            role="button"
            tabIndex={0}
            onClick={() => history.push(section.redirect)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                history.push(section.redirect);
              }
            }}
            style={{
              borderColor: section.color,
              backgroundColor: section.color + '10',
            }}
          >
            <div className="ctn">
              {section.icon}
              <p>{t(section.name)}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
};
